import { useHotel } from "@/hooks/useHotel";
import {
  PartnerProviderLogsFilters,
  PartnerProviderLogsResponse,
} from "@/types/partner-provider-log";
import type { PartnerProviderType } from "@/types/partner-settings";
import type { PartnerRevenueAnalyticsPeriod } from "@/types/partner-revenue-analytics";
import {
  PartnerDestination,
  PartnerSettings,
  UpdatePartnerSettingsRequest,
  PartnerServicesFilters,
  PartnerServicesListResponse,
  PartnerServiceFilterOptions,
} from "@/types/partner-settings";
import { partnerSettingsService } from "@/services/partner-settings-service";
import { partnerRegionFlagKeys } from "@/components/pages/guesteria-partners/hooks/usePartnerRegionFlag";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { is404 } from "@/utils/httpErrors";

export const partnerSettingsKeys = {
  all: ["partnerSettings"] as const,

  settings: (hotelId: string) => [...partnerSettingsKeys.all, hotelId] as const,

  logs: (hotelId: string, filters: PartnerProviderLogsFilters) =>
    [...partnerSettingsKeys.all, "logs", hotelId, filters] as const,

  destinations: (hotelId: string, query: string) =>
    [...partnerSettingsKeys.all, "destinations", hotelId, query] as const,

  revenueAnalytics: (
    hotelId: string,
    period: PartnerRevenueAnalyticsPeriod,
    provider: PartnerProviderType | string,
  ) =>
    [
      ...partnerSettingsKeys.all,
      "revenueAnalytics",
      hotelId,
      period,
      provider,
    ] as const,

  ordersSummary: (
    hotelId: string,
    period: PartnerRevenueAnalyticsPeriod,
    provider: PartnerProviderType | string,
  ) =>
    [
      ...partnerSettingsKeys.all,
      "ordersSummary",
      hotelId,
      period,
      provider,
    ] as const,

  services: (hotelId: string) =>
    [...partnerSettingsKeys.all, "services", hotelId] as const,

  servicesList: (hotelId: string, filters?: PartnerServicesFilters) =>
    [...partnerSettingsKeys.services(hotelId), "list", filters] as const,

  servicesFilterOptions: (hotelId: string) =>
    [...partnerSettingsKeys.services(hotelId), "filterOptions"] as const,
};

export const useGetPartnerSettings = () => {
  const { hotelId } = useHotel();

  return useQuery<PartnerSettings | null>({
    queryKey: partnerSettingsKeys.settings(hotelId || ""),
    enabled: !!hotelId,
    queryFn: async () => {
      try {
        return await partnerSettingsService.getPartnerSettings(hotelId!);
      } catch (e) {
        if (is404(e)) return null;
        throw e;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => !is404(error) && failureCount < 2,
  });
};

export const useSearchDestinations = (query: string) => {
  const { hotelId } = useHotel();

  return useQuery<PartnerDestination[]>({
    queryKey: partnerSettingsKeys.destinations(hotelId || "", query),
    enabled: !!hotelId && query.length > 0,
    queryFn: () => partnerSettingsService.searchDestinations(hotelId!, query),
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetPartnerLogs = (filters: PartnerProviderLogsFilters) => {
  const { hotelId } = useHotel();

  return useQuery<PartnerProviderLogsResponse>({
    queryKey: partnerSettingsKeys.logs(hotelId || "", filters),
    enabled: !!hotelId,
    queryFn: () => partnerSettingsService.getPartnerLogs(hotelId!, filters),
    placeholderData: (previousData) => previousData,
  });
};

export const useTriggerPartnerSync = () => {
  const { hotelId } = useHotel();
  const qc = useQueryClient();

  return useMutation<void, unknown, void>({
    mutationFn: async () => {
      if (!hotelId) throw new Error("hotelId is required");
      return partnerSettingsService.triggerSync(hotelId);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({
        queryKey: partnerSettingsKeys.settings(hotelId),
      });
      qc.invalidateQueries({
        queryKey: partnerRegionFlagKeys.byHotel(hotelId),
      });
      qc.invalidateQueries({
        queryKey: [...partnerSettingsKeys.all, "logs", hotelId],
      });
    },
  });
};

export const useUpdatePartnerSettings = () => {
  const { hotelId } = useHotel();
  const qc = useQueryClient();

  return useMutation<PartnerSettings, unknown, UpdatePartnerSettingsRequest>({
    mutationFn: async (data) => {
      if (!hotelId) throw new Error("hotelId is required");
      return partnerSettingsService.updatePartnerSettings(hotelId, data);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({
        queryKey: partnerSettingsKeys.settings(hotelId),
      });
      qc.invalidateQueries({
        queryKey: partnerRegionFlagKeys.byHotel(hotelId),
      });
    },
  });
};

export const useGetPartnerServices = (filters?: PartnerServicesFilters) => {
  const { hotelId } = useHotel();

  return useQuery<PartnerServicesListResponse>({
    queryKey: partnerSettingsKeys.servicesList(hotelId || "", filters),
    enabled: !!hotelId,
    queryFn: () => partnerSettingsService.getPartnerServices(hotelId!, filters),
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetPartnerServicesFilterOptions = () => {
  const { hotelId } = useHotel();

  return useQuery<PartnerServiceFilterOptions>({
    queryKey: partnerSettingsKeys.servicesFilterOptions(hotelId || ""),
    enabled: !!hotelId,
    queryFn: () =>
      partnerSettingsService.getPartnerServicesFilterOptions(hotelId!),
    staleTime: 10 * 60 * 1000,
  });
};
